import {
  createPath,
  LoaderFunction,
  LoaderFunctionArgs,
  matchRoutes,
  redirect,
  useBlocker,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { AccountHolder, useAccount } from "../Account";
import { LS2PacRouteObject, routes } from "../App";
import { useCallback, useEffect } from "react";
import { LoginClientManager } from "@tlc/ls2pac/app/ClientManagers/LoginClientManager";
import { getLoginRedirect } from "./getLoginRedirect";
import { useLocationConfiguration } from "../LocationConfiguration";
import React from "react";
import {
  AudienceHolder,
  AudienceType,
  ResultsViewHolder,
} from "../pages/search/ls/LSSearch";
import { useEvent } from "../../hooks/useEvents";
import { LS2PAC_CONSTANTS } from "../../../main/constants";

function isSecuredPath(allRoutes: LS2PacRouteObject[], pathname: string) {
  const matches = matchRoutes(allRoutes, pathname);
  return Boolean(matches?.some((m) => m.route.handle?.secured));
}

// route loader wrapper to handle hard-reloads or cross-origin navigations that the blocker misses
export function secureLoader(loader?: LoaderFunction) {
  return async (args: LoaderFunctionArgs) => {
    const { isAuthenticated } = await AccountHolder.fetchAccountData();
    if (!isAuthenticated) {
      // TODO use SSO preLoginFrom cookie for cancel handling
      return redirect(getLoginRedirect(args.request));
    } else if (loader) {
      // if we're authenticated return the original loader function
      return loader(args);
    } else {
      // if we don't have a loader just return null to pass through
      return null;
    }
  };
}

export function useSignInWithGoogleRedirect() {
  const { locationConfiguration } = useLocationConfiguration();
  const { isDefault, name } = locationConfiguration;
  const configSearchParam = isDefault ? "/" : `/?config=${name}`; // leading slash required
  return useCallback(
    (reactRouterPath: string) => {
      // Important, requires leading forward slash and ? to append google query params
      return `${configSearchParam}#${reactRouterPath}${
        reactRouterPath.indexOf("?") === -1 ? "?" : ""
      }`;
    },
    [locationConfiguration]
  );
}

export function AuthGuardProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAccount();
  const navigate = useNavigate();
  const curr = useLocation();
  const getSignInWithGoogleRedirect = useSignInWithGoogleRedirect();

  // Block only when target is secured and user not authed
  // only works for navigations within the SPA, loader needed for hard-reloads or cross-origin navigations
  const blocker = useBlocker(
    ({ nextLocation }) =>
      !isAuthenticated && isSecuredPath(routes, nextLocation.pathname)
  );

  const prevAuthenticatedRef = React.useRef<boolean>(isAuthenticated);
  useEffect(() => {
    if (!isAuthenticated && prevAuthenticatedRef?.current) {
      AudienceHolder.setAudience(AudienceType.AllAges);
      ResultsViewHolder.setResultsView(null);
    }
    prevAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    if (blocker.state !== "blocked") return;
    let cancelled = false;

    (async () => {
      const next = blocker.location; // destination from the blocked nav

      // Important, path needs a leading slash, will be written to SAVED_DESTINATION and used as a redirect for Google Sign In
      const loginTo = getSignInWithGoogleRedirect(createPath(next));
      // Will be written to SAVED_ORIGIN and be the restored state when the user clicks cancel, must be a react-router path (no /#)
      const loginFrom = `${createPath(curr)}`; // react-router path,  is not needed, it's passed to navigate

      // TODO refactor usage of LoginClientManager.promptIfNotLoggedIn in LS2 Pac
      LoginClientManager.promptIfNotLoggedIn(
        () => {
          if (cancelled) return;
          blocker.proceed(); // proceed to the originally requested route (loginTo)
        },
        () => {
          if (cancelled) return;
          blocker.reset(); // remain on the current route (loginFrom)
        },
        loginTo,
        loginFrom
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [blocker, curr, navigate]);

  const handleOnHome = React.useCallback(() => {
    console.log("onHome event fired, redirecting");
    navigate("/");
  }, [navigate]);
  useEvent(LS2PAC_CONSTANTS.Events.onHome, handleOnHome);

  return children;
}
